import React, { useContext } from 'react';
import { Box, Button, IconButton, Typography, alpha, useTheme } from '@mui/material';

import InputSend from './input-send';
import InputText from './input-text';
import InputAttach from './input-attach';
import { InputContext } from '../chat-input';
import InputLinearLoading from './input-linear-loading';
import UploaderComponent from '../../uploader/uploader';
import FileChip from './file-chip';
import { MessageFileItem } from '../../../type/chat.types';

const InputMain = () => {
    const theme = useTheme();
    const inputHook = useContext(InputContext);
    const {
        isDisabled,
        isMessageError,
        lengthError,
        fileHolderText,
        submitLabel,
        isNewTicket,
        onSend,
        onProcessFiles,
        pendingFilesCount,
        replyTo,
        onCancelReply,
        attachmentData,
    } = inputHook!;

    if (isNewTicket) {
        return (
            <Box sx={{ px: 0, pb: 0 }}>
                <InputText isNewTicket />

                <Box sx={{ mt: 1.25 }}>
                    <UploaderComponent
                        label={
                            fileHolderText ??
                            'برای تیکت خود یک یا چند فایل بارگذاری کنید'
                        }
                        onProcessFiles={onProcessFiles}
                    />
                </Box>

                {!!attachmentData?.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.75,
                            mt: 1,
                            direction: 'rtl',
                        }}>
                        {attachmentData.map(
                            (file: MessageFileItem, i: number) => (
                                <FileChip
                                    key={file.local_id ?? i}
                                    file={file}
                                />
                            ),
                        )}
                    </Box>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    disabled={isDisabled || pendingFilesCount > 0}
                    onClick={() => onSend()}
                    sx={{ mt: 1.75 }}>
                    {pendingFilesCount > 0
                        ? 'در حال بارگذاری پیوست...'
                        : submitLabel ?? 'ارسال تیکت'}
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: '7px',
                bgcolor: 'background.default',
                flexShrink: 0,
            }}>
            {replyTo && (
                <Box
                    sx={{
                        mb: 0.75,
                        px: 1.25,
                        py: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.06),
                        borderRight: '3px solid',
                        borderRightColor: 'primary.main',
                    }}>
                    <i className="fa-light fa-reply" style={{ fontSize: 14 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            پاسخ به{' '}
                            {replyTo.user?.isAdmin
                                ? 'کارشناس پشتیبانی'
                                : [replyTo.user?.name, replyTo.user?.family]
                                      .filter(Boolean)
                                      .join(' ') || 'کاربر'}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {replyTo.text}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onCancelReply} sx={{ width: 28, height: 28 }}>
                        <i className="fa-light fa-xmark" style={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            )}

            {isMessageError && <InputLinearLoading />}

            {!!attachmentData?.length && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 0.6,
                        overflowX: 'auto',
                        mb: 0.75,
                        px: 0.25,
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}>
                    {attachmentData.map((file: MessageFileItem, i: number) => (
                        <FileChip key={file.local_id ?? i} file={file} />
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 0.5,
                    p: 0.65,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: lengthError ? 'error.main' : 'divider',
                    borderRadius: 2.5,
                    boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 8px 24px rgba(20,25,40,.05)',
                    transition: 'border-color .15s ease, box-shadow .15s ease',
                    '&:focus-within': {
                        borderColor: 'primary.main',
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
                    },
                }}>
                <InputSend />
                <InputText isNewTicket={false} />
                <InputAttach />
            </Box>
        </Box>
    );
};

export default InputMain;
